import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { ProposalBuilderData, ProposalNodeSet } from '../../types';
import { speciesTimberKitBillable } from '../../utils/proposalSpeciesProduction';
import {
    computeProposalGrandTotals,
    getAppliedSignedStructureSnapshot,
    getClientVisibleNodeSets,
    getSignedGrandTotalsDisplay,
    allocateFrozenLine,
    calculateProposalNodeBucketsWithSelections,
    clientStructureTimberKitForDisplay,
    proposalHasClientOptionalStructures,
} from '../../utils/proposalPublicGrandTotals';
import {
    structureHasIncludedDecking,
    structureDeckingIncludedLabel,
} from '../../utils/proposalStructureDecking';
import {
    formatClientTimberSpecLabel,
    timberListForSpeciesSelection,
} from '../../utils/proposalTimberDefaults';
import { listExclusiveStructures, structureIsExclusive } from '../../utils/proposalExclusiveGroups';
import { bondingClause, bondingLineDescription, bondingLineLabel } from '../../utils/proposalBonding';

// Using built-in Helvetica font (external font URLs can cause DataView errors)

// Styles for PDF
const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontFamily: 'Helvetica',
        fontSize: 10,
        color: '#1a1a1a',
        backgroundColor: '#ffffff',
    },
    coverPage: {
        padding: 60,
        fontFamily: 'Helvetica',
        backgroundColor: '#2a392d',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100%',
    },
    coverLogo: {
        width: 200,
        height: 'auto',
        marginBottom: 40,
    },
    coverCompanyName: {
        fontSize: 28,
        fontWeight: 700,
        color: '#ffffff',
        marginBottom: 8,
        textAlign: 'center',
    },
    coverSubtitle: {
        fontSize: 14,
        color: 'rgba(255, 255, 255, 0.7)',
        marginBottom: 60,
        textAlign: 'center',
    },
    coverClientSection: {
        marginTop: 40,
        padding: 30,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 8,
        width: '100%',
        maxWidth: 400,
    },
    coverClientCompany: {
        fontSize: 12,
        fontWeight: 600,
        color: 'rgba(255, 255, 255, 0.6)',
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 4,
        textAlign: 'center',
    },
    coverClientName: {
        fontSize: 22,
        fontWeight: 600,
        color: '#ffffff',
        marginBottom: 6,
        textAlign: 'center',
    },
    coverProjectName: {
        fontSize: 14,
        color: 'rgba(255, 255, 255, 0.8)',
        marginBottom: 6,
        fontStyle: 'italic',
        textAlign: 'center',
    },
    coverLocation: {
        fontSize: 11,
        color: 'rgba(255, 255, 255, 0.5)',
        marginBottom: 12,
        textAlign: 'center',
    },
    coverDate: {
        fontSize: 10,
        color: 'rgba(255, 255, 255, 0.4)',
        textAlign: 'center',
    },
    header: {
        marginBottom: 30,
        borderBottom: '2px solid #2C3E2F',
        paddingBottom: 20,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    headerLeft: {
        flexDirection: 'column',
    },
    logo: {
        width: 120,
        height: 'auto',
        marginBottom: 15,
    },
    title: {
        fontSize: 24,
        fontWeight: 700,
        color: '#1e293b',
        marginBottom: 4,
    },
    subtitle: {
        fontSize: 12,
        color: '#64748b',
    },
    clientSection: {
        marginBottom: 30,
        backgroundColor: '#f8fafc',
        padding: 15,
        borderRadius: 4,
    },
    clientName: {
        fontSize: 16,
        fontWeight: 600,
        color: '#0f172a',
        marginBottom: 4,
    },
    clientDetails: {
        fontSize: 10,
        color: '#475569',
    },
    sectionTitle: {
        fontSize: 14,
        fontWeight: 700,
        color: '#1e293b',
        marginBottom: 12,
        marginTop: 20,
        borderBottom: '1px solid #e2e8f0',
        paddingBottom: 6,
    },
    summaryTable: {
        marginBottom: 20,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#1e293b',
        padding: 10,
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
    },
    tableHeaderCell: {
        color: '#ffffff',
        fontSize: 10,
        fontWeight: 600,
    },
    tableRow: {
        flexDirection: 'row',
        padding: 10,
        borderBottom: '1px solid #e2e8f0',
        backgroundColor: '#ffffff',
    },
    tableRowAlt: {
        flexDirection: 'row',
        padding: 10,
        borderBottom: '1px solid #e2e8f0',
        backgroundColor: '#f8fafc',
    },
    tableCell: {
        fontSize: 10,
        color: '#334155',
    },
    tableCellRight: {
        fontSize: 10,
        color: '#334155',
        textAlign: 'right',
    },
    totalRow: {
        flexDirection: 'row',
        padding: 12,
        backgroundColor: '#1e293b',
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4,
    },
    totalLabel: {
        color: '#ffffff',
        fontSize: 12,
        fontWeight: 700,
    },
    totalValue: {
        color: '#ffffff',
        fontSize: 12,
        fontWeight: 700,
        textAlign: 'right',
    },
    structureCard: {
        marginBottom: 15,
        border: '1px solid #e2e8f0',
        borderRadius: 4,
        overflow: 'hidden',
        breakInside: 'avoid',
    },
    structureHeader: {
        backgroundColor: '#f1f5f9',
        padding: 10,
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    structureName: {
        fontSize: 11,
        fontWeight: 600,
        color: '#0f172a',
    },
    structureQty: {
        fontSize: 10,
        color: '#64748b',
    },
    structureDetails: {
        padding: 10,
    },
    detailRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 4,
    },
    detailLabel: {
        fontSize: 9,
        color: '#64748b',
    },
    detailValue: {
        fontSize: 9,
        color: '#334155',
        fontWeight: 600,
    },
    speciesSection: {
        marginTop: 20,
        marginBottom: 20,
        padding: 15,
        backgroundColor: '#fef3c7',
        borderRadius: 4,
        border: '1px solid #f59e0b',
    },
    speciesTitle: {
        fontSize: 12,
        fontWeight: 600,
        color: '#92400e',
        marginBottom: 10,
    },
    speciesOption: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 6,
        borderBottom: '1px solid #fcd34d',
    },
    speciesName: {
        fontSize: 10,
        color: '#78350f',
    },
    speciesPrice: {
        fontSize: 10,
        fontWeight: 600,
        color: '#78350f',
    },
    termsSection: {
        marginTop: 30,
        padding: 15,
        backgroundColor: '#f8fafc',
        borderRadius: 4,
    },
    termsTitle: {
        fontSize: 11,
        fontWeight: 600,
        color: '#334155',
        marginBottom: 8,
    },
    termsText: {
        fontSize: 8,
        color: '#64748b',
        lineHeight: 1.4,
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 40,
        right: 40,
        borderTop: '1px solid #e2e8f0',
        paddingTop: 10,
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    footerText: {
        fontSize: 8,
        color: '#94a3b8',
    },
    pageNumber: {
        fontSize: 8,
        color: '#94a3b8',
    },
    col1: { width: '50%' },
    col2: { width: '25%' },
    col3: { width: '25%' },
    chooseOneBox: {
        marginBottom: 12,
        padding: 10,
        backgroundColor: '#f3f6f3',
        borderRadius: 4,
        borderWidth: 1,
        borderColor: '#2C3E2F',
    },
    chooseOneTitle: {
        fontSize: 9,
        fontWeight: 600,
        color: '#2C3E2F',
        marginBottom: 4,
    },
    chooseOneHint: {
        fontSize: 8,
        color: '#3d5241',
        lineHeight: 1.4,
        marginBottom: 8,
    },
    chooseOneRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 3,
        borderTopWidth: 1,
        borderTopColor: '#d5ddd6',
    },
    chooseOneMark: {
        fontSize: 8,
        color: '#2C3E2F',
        width: 18,
    },
    chooseOneName: {
        fontSize: 9,
        color: '#334155',
        flex: 1,
    },
    chooseOneNameSelected: {
        fontWeight: 600,
        color: '#0f172a',
    },
    chooseOneQty: {
        fontSize: 8,
        color: '#64748b',
        marginRight: 8,
    },
    chooseOneStatus: {
        fontSize: 8,
        fontWeight: 600,
    },
    chooseOneStatusOn: {
        color: '#2C3E2F',
    },
    chooseOneStatusOff: {
        color: '#94a3b8',
    },
    structureHeaderLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        paddingRight: 8,
    },
    structureBadge: {
        backgroundColor: '#e8eee9',
        borderWidth: 1,
        borderColor: '#2C3E2F',
        borderRadius: 2,
        paddingVertical: 2,
        paddingHorizontal: 5,
        marginLeft: 6,
    },
    structureBadgeText: {
        fontSize: 7,
        fontWeight: 700,
        color: '#2C3E2F',
    },
});

interface ProposalPdfProps {
    proposal: ProposalBuilderData;
    companyLogo?: string | null;
    companyName?: string;
    companyWebsite?: string;
    includeInstall?: boolean;
    selectedSpecies?: Record<string, string>;
    kitSelectedSpecies?: string;  // For kit proposals - the selected species
    structureSelections?: Record<string, boolean>;
    generatedDate?: Date;
    /** Override cover / prepared-for client name (e.g. sample invite display name). */
    preparedForName?: string | null;
    /** Override cover / prepared-for company (e.g. sample invite company). */
    preparedForCompany?: string | null;
    /** Project label for the cover, footer, and download name. Never the public URL slug. */
    projectLabel?: string | null;
    // For signed PDFs
    signature?: {
        dataUrl: string;
        signedBy: string;
        signedAt: Date | { toDate: () => Date };
    };
    showOnlySelected?: boolean;  // Hide unselected species options for signed PDFs
}

// Helper to format currency
const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

function pdfNameToken(value: string): string {
    return value.trim().replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** Browser download name. Uses the current client and project, not the public slug. */
export function proposalPdfDownloadName(options: {
    clientName?: string | null;
    projectName?: string | null;
    signed?: boolean;
}): string {
    const base = [pdfNameToken(options.clientName || ''), pdfNameToken(options.projectName || '')]
        .filter(Boolean)
        .join('-') || 'proposal';
    return options.signed ? `${base}-signed.pdf` : `${base}-investment-proposal.pdf`;
}

const calculateBuckets = (
    nodeSet: ProposalNodeSet,
    profitMargin: number,
    proposal: ProposalBuilderData,
    structureSelections?: Record<string, boolean>,
) => calculateProposalNodeBucketsWithSelections(nodeSet, profitMargin, proposal, structureSelections);

const ProposalPdfDocument: React.FC<ProposalPdfProps> = ({
    proposal,
    companyLogo,
    companyName = "Leiper's Creek Timber Frames",
    companyWebsite = 'www.lctimberframes.com',
    includeInstall = true,
    selectedSpecies = {},
    kitSelectedSpecies,
    structureSelections,
    generatedDate = new Date(),
    preparedForName,
    preparedForCompany,
    projectLabel,
    signature,
    showOnlySelected = false,
}) => {
    const coverClientName = (preparedForName || '').trim() || proposal.clientName || '';
    const pdfProjectLabel = (
        projectLabel
        || proposal.descriptor
        || proposal.projectName
        || ''
    ).trim();
    const footerIdentity = [coverClientName, pdfProjectLabel].filter(Boolean).join(' · ');
    const proposalFooterLabel = footerIdentity ? `Proposal: ${footerIdentity}` : 'Proposal';
    const signedFooterLabel = footerIdentity ? `Signed Proposal: ${footerIdentity}` : 'Signed Proposal';
    const coverLeadCompany =
        preparedForCompany !== undefined
            ? (preparedForCompany || '').trim() || undefined
            : (proposal.leadCompany || '').trim() || undefined;
    const visibleNodeSets = getClientVisibleNodeSets(proposal, structureSelections);
    const signedStructureSnapshot = getAppliedSignedStructureSnapshot(proposal);
    const exclusivePool = signedStructureSnapshot
        ? (proposal.nodeSets || []).filter(ns => ns.id in signedStructureSnapshot)
        : (proposal.nodeSets || []);
    const exclusiveStructures = listExclusiveStructures(exclusivePool);
    const hasExclusiveChoice = exclusiveStructures.length > 1;
    const exclusiveSelectedId = hasExclusiveChoice
        ? exclusiveStructures.find(ns => {
            if (signedStructureSnapshot) return signedStructureSnapshot[ns.id] === true;
            if (!structureSelections) return ns.id === exclusiveStructures[0].id;
            return structureSelections[ns.id] !== false;
        })?.id ?? exclusiveStructures[0].id
        : null;
    const shouldIncludeInstall = !proposal.installOptional || includeInstall;

    const liveTotals = computeProposalGrandTotals(proposal, {
        includeInstall,
        selectedSpecies,
        kitSelectedSpecies,
        structureSelections,
    });
    const signedTotals = getSignedGrandTotalsDisplay(proposal);
    // After signing, later edits to install or travel must not change the contract PDF.
    const grandTotals = signedTotals
        ? {
            ...liveTotals,
            design: signedTotals.design,
            timberKit: signedTotals.timberKit,
            install: signedTotals.install,
            bonding: signedTotals.bonding,
            bondingPercent: signedTotals.bondingPercent,
            discountAmount: signedTotals.discountAmount,
            total: signedTotals.total,
            travel: liveTotals.travel,
        }
        : liveTotals;
    const installationIncludesTravel = visibleNodeSets.some(ns => (ns.travel?.total || 0) > 0) || grandTotals.travel > 0;
    const liveStructureBuckets = visibleNodeSets.map(ns => calculateBuckets(ns, proposal.profitMargin, proposal, structureSelections));
    const frozenDesignShares = signedTotals
        ? allocateFrozenLine(liveStructureBuckets.map(b => b.design), grandTotals.design)
        : null;
    const frozenInstallShares = signedTotals
        ? allocateFrozenLine(liveStructureBuckets.map(b => b.install), grandTotals.install)
        : null;
    const liveTimberWeights = visibleNodeSets.map((ns, idx) => {
        if (proposal.isKitProposal && proposal.kitPricing && proposal.kitPricing.length > 0) {
            if (visibleNodeSets.length === 1) return grandTotals.timberKit;
            const activeSpecies = selectedSpecies[ns.id] || kitSelectedSpecies || proposal.activeSpecies || proposal.offeredSpecies?.[0] || proposal.kitPricing[0]?.speciesName || '';
            const speciesPricing = proposal.kitPricing.find(kp => kp.speciesName === activeSpecies);
            if (speciesPricing?.structurePricing && speciesPricing.structurePricing[idx]) {
                return speciesPricing.structurePricing[idx].cost || 0;
            }
            return speciesPricing?.totalCost || grandTotals.timberKit;
        }
        if (visibleNodeSets.length === 1) return grandTotals.timberKit;
        return clientStructureTimberKitForDisplay(proposal, ns, selectedSpecies, structureSelections);
    });
    const frozenTimberShares = signedTotals
        ? allocateFrozenLine(liveTimberWeights, grandTotals.timberKit)
        : null;

    // Build species options for display
    const speciesOptions: { name: string; total: number; isDefault: boolean }[] = [];
    const hasMultipleSpecies = visibleNodeSets.some(ns =>
        ns.materials.quoteMultipleSpecies && (ns.materials.alternateSpecies?.length ?? 0) > 0
    );

    if (hasMultipleSpecies) {
        const primarySpecies = visibleNodeSets[0]?.materials.timberList[0]?.species || 'Default';
        const defaultTotal = visibleNodeSets.reduce((sum, ns) => {
            const buckets = calculateBuckets(ns, proposal.profitMargin, proposal, structureSelections);
            const installAmount = shouldIncludeInstall ? buckets.install : 0;
            return sum + buckets.design + buckets.timberKit + installAmount;
        }, 0);
        speciesOptions.push({ name: primarySpecies, total: defaultTotal, isDefault: true });

        const alternateNames = new Set<string>();
        visibleNodeSets.forEach(ns => {
            (ns.materials.alternateSpecies || []).forEach(s => alternateNames.add(s.speciesName));
        });

        alternateNames.forEach(speciesName => {
            const speciesTotal = visibleNodeSets.reduce((sum, ns) => {
                const altSpecies = (ns.materials.alternateSpecies || []).find(s => s.speciesName === speciesName);
                if (altSpecies) {
                    const qty = ns.quantity || 1;
                    const buckets = calculateBuckets(ns, proposal.profitMargin, proposal, structureSelections);
                    const installAmount = shouldIncludeInstall ? buckets.install : 0;
                    const timberKit = speciesTimberKitBillable(
                        ns,
                        speciesName,
                        buckets.timberKit,
                        proposal.profitMargin,
                        qty,
                        ns.billablePrices?.speciesOptions,
                    );
                    return sum + buckets.design + timberKit + installAmount;
                }
                const buckets = calculateBuckets(ns, proposal.profitMargin, proposal, structureSelections);
                const installAmount = shouldIncludeInstall ? buckets.install : 0;
                return sum + buckets.design + buckets.timberKit + installAmount;
            }, 0);
            speciesOptions.push({ name: speciesName, total: speciesTotal, isDefault: false });
        });
    }

    const formattedDate = generatedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
    const formattedTime = generatedDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
    });

    const derivedProjectName = pdfProjectLabel;

    return (
        <Document>
            {/* Cover Page - Dark Green Background */}
            <Page size="LETTER" style={styles.coverPage}>
                {companyLogo && <Image src={companyLogo} style={styles.coverLogo} />}

                <View style={styles.coverClientSection}>
                    {coverLeadCompany && (
                        <Text style={styles.coverClientCompany}>{coverLeadCompany}</Text>
                    )}
                    <Text style={styles.coverClientName}>{coverClientName}</Text>
                    {derivedProjectName && (
                        <Text style={styles.coverProjectName}>{derivedProjectName}</Text>
                    )}
                    {proposal.location && (
                        <Text style={styles.coverLocation}>{proposal.location}</Text>
                    )}
                    <Text style={styles.coverDate}>Generated: {formattedDate} at {formattedTime}</Text>
                </View>
            </Page>

            {/* Content Pages - White Background */}
            <Page size="LETTER" style={styles.page}>


                {hasExclusiveChoice && (
                    <View style={styles.chooseOneBox} wrap={false}>
                        <Text style={styles.chooseOneTitle}>Choose one</Text>
                        <Text style={styles.chooseOneHint}>
                            {signature
                                ? 'This selection was saved when you signed the proposal. Only one of these structures is included.'
                                : 'Only one of these structures can be purchased. Pricing below includes the selected option.'}
                        </Text>
                        {exclusiveStructures.map(ns => {
                            const selected = ns.id === exclusiveSelectedId;
                            return (
                                <View key={ns.id} style={styles.chooseOneRow}>
                                    <Text style={styles.chooseOneMark}>{selected ? '[x]' : '[ ]'}</Text>
                                    <Text style={[styles.chooseOneName, selected ? styles.chooseOneNameSelected : {}]}>
                                        {ns.name || 'Structure'}
                                    </Text>
                                    {(ns.quantity || 1) > 1 && (
                                        <Text style={styles.chooseOneQty}>Qty: {ns.quantity}</Text>
                                    )}
                                    <Text
                                        style={[
                                            styles.chooseOneStatus,
                                            selected ? styles.chooseOneStatusOn : styles.chooseOneStatusOff,
                                        ]}
                                    >
                                        {selected ? 'Selected' : 'Not selected'}
                                    </Text>
                                </View>
                            );
                        })}
                    </View>
                )}

                {/* Investment Summary - keep header and table together */}
                <View wrap={false}>
                    {proposal.isRomPricing && (
                        <View style={{ marginBottom: 12, padding: 10, backgroundColor: '#fef3c7', borderRadius: 4, borderWidth: 1, borderColor: '#f59e0b' }}>
                            <Text style={{ fontSize: 9, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>ROM Pricing</Text>
                            <Text style={{ fontSize: 8, color: '#78350f', lineHeight: 1.4 }}>These figures are preliminary and subject to refinement after client input.</Text>
                        </View>
                    )}
                    {!signature && proposalHasClientOptionalStructures(proposal) && (
                        <View style={{ marginBottom: 12, padding: 10, backgroundColor: '#faf6ec', borderRadius: 4, borderWidth: 1, borderColor: '#c9a227' }}>
                            <Text style={{ fontSize: 9, fontWeight: 600, color: '#8a6d1c', marginBottom: 4 }}>About Optional Structures</Text>
                            <Text style={{ fontSize: 8, color: '#6b5416', lineHeight: 1.4 }}>
                                Pricing reflects the efficiency of building these structures as one project — removing optional structures will adjust the remaining structure totals.
                            </Text>
                        </View>
                    )}
                    <Text style={styles.sectionTitle}>Investment Summary - Selected Options</Text>
                    <View style={styles.summaryTable}>
                        <View style={styles.tableHeader}>
                            <Text style={[styles.tableHeaderCell, styles.col1]}>Category</Text>
                            <Text style={[styles.tableHeaderCell, styles.col2]}>Description</Text>
                            <Text style={[styles.tableHeaderCell, styles.col3, { textAlign: 'right' }]}>Amount</Text>
                        </View>

                        <View style={styles.tableRow}>
                            <Text style={[styles.tableCell, styles.col1]}>Design & Engineering</Text>
                            <Text style={[styles.tableCell, styles.col2]}>
                                {(proposal.isKitProposal || proposal.designIncludedInKit) ? 'Included in Timber Kit' : 'Complete design package'}
                            </Text>
                            <Text style={[styles.tableCellRight, styles.col3]}>
                                {(proposal.isKitProposal || proposal.designIncludedInKit) ? 'Included' : formatCurrency(grandTotals.design)}
                            </Text>
                        </View>

                        <View style={styles.tableRowAlt}>
                            <Text style={[styles.tableCell, styles.col1]}>Timber Kit</Text>
                            <Text style={[styles.tableCell, styles.col2]}>{(proposal.isKitProposal || proposal.designIncludedInKit) ? 'Design, materials, production & freight' : 'Materials, production & freight'}</Text>
                            <Text style={[styles.tableCellRight, styles.col3]}>{formatCurrency(grandTotals.timberKit)}</Text>
                        </View>

                        {/* Species selections per structure */}
                        {visibleNodeSets.map((ns, idx) => {
                            // For kit proposals, use per-structure pricing from kitPricing.structurePricing
                            if (proposal.isKitProposal && proposal.kitPricing && proposal.kitPricing.length > 0) {
                                const activeSpecies = selectedSpecies[ns.id] || kitSelectedSpecies || proposal.activeSpecies || proposal.offeredSpecies?.[0] || proposal.kitPricing[0]?.speciesName || '';
                                const speciesPricing = proposal.kitPricing?.find(kp => kp.speciesName === activeSpecies);

                                // One structure left (optional building dropped): show the
                                // summary timber kit, which already carries the shared costs.
                                let timberKitCost = frozenTimberShares
                                    ? frozenTimberShares[idx]
                                    : grandTotals.timberKit;

                                if (!frozenTimberShares && visibleNodeSets.length > 1 && speciesPricing?.structurePricing && speciesPricing.structurePricing[idx]) {
                                    timberKitCost = speciesPricing.structurePricing[idx].cost || 0;
                                }

                                return (
                                    <View key={ns.id || idx} style={{ flexDirection: 'column', backgroundColor: '#f8fafc' }}>
                                        <View style={{ flexDirection: 'row', padding: '4px 10px' }}>
                                            <Text style={[styles.tableCell, styles.col1, { fontSize: 8, color: '#64748b', paddingLeft: 10 }]}>
                                                └ {ns.name || `Structure ${idx + 1}`}
                                            </Text>
                                            <Text style={[styles.tableCell, styles.col2, { fontSize: 8, color: '#64748b' }]}>
                                                {formatClientTimberSpecLabel(timberListForSpeciesSelection(ns.materials, activeSpecies), activeSpecies)}
                                            </Text>
                                            <Text style={[styles.tableCellRight, styles.col3, { fontSize: 8, color: '#64748b' }]}>
                                                {formatCurrency(timberKitCost)}
                                            </Text>
                                        </View>
                                        {structureHasIncludedDecking(ns.materials) && (
                                            <View style={{ flexDirection: 'row', padding: '2px 10px 4px 18px' }}>
                                                <Text style={[styles.tableCell, styles.col1, { fontSize: 7, color: '#4a6741' }]}>
                                                    Roof Decking
                                                </Text>
                                                <Text style={[styles.tableCell, styles.col2, { fontSize: 7, color: '#4a6741', fontStyle: 'italic' }]}>
                                                    {structureDeckingIncludedLabel(ns.materials)}
                                                </Text>
                                                <Text style={[styles.tableCellRight, styles.col3, { fontSize: 7, color: '#4a6741' }]}>
                                                    Included
                                                </Text>
                                            </View>
                                        )}
                                    </View>
                                );
                            }

                            // For custom proposals, use per-structure species
                            const structureSelected = selectedSpecies[ns.id];
                            const speciesName = structureSelected && structureSelected !== 'default'
                                ? structureSelected
                                : (ns.materials.timberList[0]?.species || 'Default');
                            const timberKitCost = frozenTimberShares
                                ? frozenTimberShares[idx]
                                : (visibleNodeSets.length === 1
                                    ? grandTotals.timberKit
                                    : clientStructureTimberKitForDisplay(
                                        proposal,
                                        ns,
                                        selectedSpecies,
                                        structureSelections,
                                    ));

                            return (
                                <View key={ns.id || idx} style={{ flexDirection: 'column', backgroundColor: '#f8fafc' }}>
                                    <View style={{ flexDirection: 'row', padding: '4px 10px' }}>
                                        <Text style={[styles.tableCell, styles.col1, { fontSize: 8, color: '#64748b', paddingLeft: 10 }]}>
                                            └ {ns.name || `Structure ${idx + 1}`}
                                        </Text>
                                        <Text style={[styles.tableCell, styles.col2, { fontSize: 8, color: '#64748b' }]}>
                                            {formatClientTimberSpecLabel(timberListForSpeciesSelection(ns.materials, structureSelected), speciesName)}
                                        </Text>
                                        <Text style={[styles.tableCellRight, styles.col3, { fontSize: 8, color: '#64748b' }]}>
                                            {formatCurrency(timberKitCost)}
                                        </Text>
                                    </View>
                                    {structureHasIncludedDecking(ns.materials) && (
                                        <View style={{ flexDirection: 'row', padding: '2px 10px 4px 18px' }}>
                                            <Text style={[styles.tableCell, styles.col1, { fontSize: 7, color: '#4a6741' }]}>
                                                Roof Decking
                                            </Text>
                                            <Text style={[styles.tableCell, styles.col2, { fontSize: 7, color: '#4a6741', fontStyle: 'italic' }]}>
                                                {structureDeckingIncludedLabel(ns.materials)}
                                            </Text>
                                            <Text style={[styles.tableCellRight, styles.col3, { fontSize: 7, color: '#4a6741' }]}>
                                                Included
                                            </Text>
                                        </View>
                                    )}
                                </View>
                            );
                        })}

                        {shouldIncludeInstall && (
                            <View style={styles.tableRow}>
                                <Text style={[styles.tableCell, styles.col1]}>Installation</Text>
                                <Text style={[styles.tableCell, styles.col2]}>On-site installation{installationIncludesTravel ? ' & travel' : ''}</Text>
                                <Text style={[styles.tableCellRight, styles.col3]}>
                                    {grandTotals.install === 0 ? 'Not Included' : formatCurrency(grandTotals.install)}
                                </Text>
                            </View>
                        )}

                        {grandTotals.bonding > 0 && (
                            <View style={styles.tableRow}>
                                <Text style={[styles.tableCell, styles.col1]}>{bondingLineLabel(proposal.globalCosts?.bonding)}</Text>
                                <Text style={[styles.tableCell, styles.col2]}>{bondingLineDescription(proposal.globalCosts?.bonding)}</Text>
                                <Text style={[styles.tableCellRight, styles.col3]}>{formatCurrency(grandTotals.bonding)}</Text>
                            </View>
                        )}

                        {grandTotals.discountAmount > 0 && (
                            <View style={styles.tableRow}>
                                <Text style={[styles.tableCell, styles.col1]}>
                                    {proposal.discount?.label?.trim() || 'Discount'}
                                </Text>
                                <Text style={[styles.tableCell, styles.col2]}>
                                    {proposal.discount?.clientDescription?.trim() || 'Promotional discount'}
                                </Text>
                                <Text style={[styles.tableCellRight, styles.col3, { color: '#15803d' }]}>
                                    −{formatCurrency(grandTotals.discountAmount)}
                                </Text>
                            </View>
                        )}

                        <View style={styles.totalRow}>
                            <Text style={[styles.totalLabel, styles.col1]}>Total Investment</Text>
                            <Text style={[styles.totalLabel, styles.col2]}></Text>
                            <Text style={[styles.totalValue, styles.col3]}>{formatCurrency(grandTotals.total)}</Text>
                        </View>
                    </View>
                </View>

                {/* Structure Details - starts on new page */}
                <View break>
                    <Text style={styles.sectionTitle}>Structure Details</Text>
                </View>
                {visibleNodeSets.map((ns, idx) => {
                    const buckets = calculateBuckets(ns, proposal.profitMargin, proposal, structureSelections);
                    const defaultSpecies = ns.materials.timberList[0]?.species || 'Default';
                    const hasAlternates = ns.materials.quoteMultipleSpecies && (ns.materials.alternateSpecies?.length ?? 0) > 0;

                    // For kit proposals, use kitSelectedSpecies and kitPricing
                    // For custom proposals, use per-structure selectedSpecies
                    let selectedSpeciesName = defaultSpecies;
                    let actualTimberKitCost = buckets.timberKit;

                    if (proposal.isKitProposal && proposal.kitPricing && proposal.kitPricing.length > 0) {
                        // Use kit pricing for kit proposals - get per-structure pricing
                        const activeSpecies = selectedSpecies[ns.id] || kitSelectedSpecies || proposal.activeSpecies || proposal.offeredSpecies?.[0] || proposal.kitPricing[0]?.speciesName || '';
                        selectedSpeciesName = activeSpecies || defaultSpecies;

                        const speciesPricing = proposal.kitPricing.find(kp => kp.speciesName === activeSpecies);

                        if (visibleNodeSets.length === 1) {
                            actualTimberKitCost = grandTotals.timberKit;
                        } else if (speciesPricing?.structurePricing && speciesPricing.structurePricing[idx]) {
                            actualTimberKitCost = speciesPricing.structurePricing[idx].cost || 0;
                        } else {
                            actualTimberKitCost = speciesPricing?.totalCost || grandTotals.timberKit;
                        }
                    } else {
                        // Standard per-structure species for custom proposals
                        const structureSelected = selectedSpecies[ns.id];
                        selectedSpeciesName = structureSelected && structureSelected !== 'default'
                            ? structureSelected
                            : defaultSpecies;

                        actualTimberKitCost = visibleNodeSets.length === 1
                            ? grandTotals.timberKit
                            : clientStructureTimberKitForDisplay(
                                proposal,
                                ns,
                                selectedSpecies,
                                structureSelections,
                            );
                    }

                    if (frozenTimberShares) actualTimberKitCost = frozenTimberShares[idx];
                    const designAmount = frozenDesignShares ? frozenDesignShares[idx] : buckets.design;
                    const installCost = frozenInstallShares ? frozenInstallShares[idx] : buckets.install;

                    // When design is a separate line (not rolled into kit), include it in the structure subtotal
                    const designSeparate =
                        !proposal.isKitProposal && !proposal.designIncludedInKit ? designAmount : 0;

                    return (
                        <View key={ns.id || idx} style={styles.structureCard} wrap={false}>
                            <View style={styles.structureHeader}>
                                <View style={styles.structureHeaderLeft}>
                                    <Text style={styles.structureName}>{ns.name || `Structure ${idx + 1}`}</Text>
                                    {hasExclusiveChoice && structureIsExclusive(ns) && (
                                        <View style={styles.structureBadge}>
                                            <Text style={styles.structureBadgeText}>CHOOSE 1</Text>
                                        </View>
                                    )}
                                </View>
                                {(ns.quantity || 1) > 1 && (
                                    <Text style={styles.structureQty}>Qty: {ns.quantity}</Text>
                                )}
                            </View>
                            {ns.notes && (
                                <Text style={{ fontSize: 9, fontStyle: 'italic', color: '#64748b', paddingHorizontal: 12, paddingTop: 4, paddingBottom: 12 }}>
                                    {ns.notes}
                                </Text>
                            )}
                            <View style={styles.structureDetails}>
                                <View style={styles.detailRow}>
                                    <Text style={styles.detailLabel}>Design</Text>
                                    <Text style={styles.detailValue}>
                                        {(proposal.isKitProposal || proposal.designIncludedInKit) ? 'Included' : formatCurrency(designAmount)}
                                    </Text>
                                </View>
                                <View style={styles.detailRow}>
                                    <Text style={styles.detailLabel}>Timber Kit - {formatClientTimberSpecLabel(timberListForSpeciesSelection(ns.materials, selectedSpeciesName), selectedSpeciesName)} (selected)</Text>
                                    <Text style={styles.detailValue}>{formatCurrency(actualTimberKitCost)}</Text>
                                </View>
                                {structureHasIncludedDecking(ns.materials) && (
                                    <View style={styles.detailRow}>
                                        <Text style={styles.detailLabel}>Roof Decking</Text>
                                        <Text style={[styles.detailValue, { fontStyle: 'italic' }]}>
                                            {structureDeckingIncludedLabel(ns.materials)}
                                        </Text>
                                    </View>
                                )}
                                {shouldIncludeInstall && (
                                    <View style={styles.detailRow}>
                                        <Text style={styles.detailLabel}>Installation</Text>
                                        <Text style={styles.detailValue}>
                                            {installCost === 0 ? 'Not Included' : formatCurrency(installCost)}
                                        </Text>
                                    </View>
                                )}
                                <View style={[styles.detailRow, { marginTop: 6, borderTop: '1px solid #e2e8f0', paddingTop: 6 }]}>
                                    <Text style={[styles.detailLabel, { fontWeight: 600, color: '#0f172a' }]}>Subtotal</Text>
                                    <Text style={[styles.detailValue, { color: '#0f172a' }]}>
                                        {formatCurrency(
                                            actualTimberKitCost +
                                                (shouldIncludeInstall ? installCost : 0) +
                                                designSeparate,
                                        )}
                                    </Text>
                                </View>

                                {/* Available Species Options for this structure - hidden in signed PDFs */}
                                {/* For kit proposals, show per-structure species options from kitPricing.structurePricing */}
                                {proposal.isKitProposal && proposal.kitPricing && proposal.kitPricing.length > 1 && !showOnlySelected && (
                                    <View style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #cbd5e1' }}>
                                        <Text style={{ fontSize: 8, color: '#64748b', marginBottom: 4 }}>Available Species Options:</Text>
                                        {proposal.kitPricing.map((kp, kpIdx) => {
                                            // Get structure-specific cost from kitPricing.structurePricing
                                            const structureCost = kp.structurePricing?.[idx]?.cost ?? kp.totalCost / proposal.nodeSets.length;
                                            const isSelected = selectedSpeciesName === kp.speciesName;
                                            const isStandard = kpIdx === 0;
                                            return (
                                                <View key={kpIdx} style={styles.detailRow}>
                                                    <Text style={{ fontSize: 8, color: isSelected ? '#2C3E2F' : '#334155', fontWeight: isSelected ? 600 : 400 }}>
                                                        {kp.speciesName}{isStandard ? ' (Standard)' : ''}{isSelected ? ' ✓' : ''}
                                                    </Text>
                                                    <Text style={{ fontSize: 8, color: '#334155' }}>{formatCurrency(structureCost)}</Text>
                                                </View>
                                            );
                                        })}
                                    </View>
                                )}
                                {!proposal.isKitProposal && hasAlternates && !showOnlySelected && (
                                    <View style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #cbd5e1' }}>
                                        <Text style={{ fontSize: 8, color: '#64748b', marginBottom: 4 }}>Available Species Options:</Text>
                                        <View style={styles.detailRow}>
                                            <Text style={{ fontSize: 8, color: selectedSpeciesName === defaultSpecies ? '#2C3E2F' : '#334155', fontWeight: selectedSpeciesName === defaultSpecies ? 600 : 400 }}>
                                                {defaultSpecies} (Standard){selectedSpeciesName === defaultSpecies ? ' ✓' : ''}
                                            </Text>
                                            <Text style={{ fontSize: 8, color: '#334155' }}>{formatCurrency(buckets.timberKit + (proposal.designIncludedInKit ? buckets.design : 0))}</Text>
                                        </View>
                                        {(ns.materials.alternateSpecies || []).map((alt, altIdx) => {
                                            const qty = ns.quantity || 1;
                                            const altTimberKit = speciesTimberKitBillable(
                                                ns,
                                                alt.speciesName,
                                                buckets.timberKit,
                                                proposal.profitMargin,
                                                qty,
                                                ns.billablePrices?.speciesOptions,
                                            ) + (proposal.designIncludedInKit ? buckets.design : 0);
                                            const isSelected = selectedSpeciesName === alt.speciesName;
                                            return (
                                                <View key={altIdx} style={styles.detailRow}>
                                                    <Text style={{ fontSize: 8, color: isSelected ? '#2C3E2F' : '#334155', fontWeight: isSelected ? 600 : 400 }}>
                                                        {alt.speciesName}{isSelected ? ' ✓' : ''}
                                                    </Text>
                                                    <Text style={{ fontSize: 8, color: '#334155' }}>{formatCurrency(altTimberKit)}</Text>
                                                </View>
                                            );
                                        })}
                                    </View>
                                )}
                            </View>
                        </View>
                    );
                })}



                {/* Footer */}
                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>{companyName} • {companyWebsite} • {proposalFooterLabel}</Text>
                    <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} style={styles.pageNumber} />
                </View>
            </Page>

            {/* Terms & Conditions Page */}
            <Page size="LETTER" style={styles.page}>
                <Text style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', marginBottom: 20 }}>Terms & Conditions</Text>

                <View style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 10, fontWeight: 600, color: '#334155', marginBottom: 4 }}>1. OVERVIEW</Text>
                    <Text style={{ fontSize: 9, color: '#475569', lineHeight: 1.4 }}>
                        This Construction Contract is being entered into between {coverClientName || '[Client Name]'} ("Owner") and MJL Enterprises, LLC dba Leiper's Creek Timber Frames ("Contractor"), TN Contractor License #78475, (collectively, the "Parties"). The effective date of this Construction Contract will be the date of the last signature. This Construction Contract, along with incorporated documents referenced herein (if any), sets forth the terms and conditions agreed to between the Parties relating to the Work by Contractor for Owner.
                    </Text>
                </View>

                <View style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 10, fontWeight: 600, color: '#334155', marginBottom: 4 }}>GENERAL LIMITATION OF LIABILITY</Text>
                    <Text style={{ fontSize: 9, color: '#475569', lineHeight: 1.4 }}>
                        Unless otherwise more narrowly limited or required by law, the total liability of Leiper's Creek Timber Frames pertaining to any Work or other activity under this Construction Contract shall be limited to re-performing, at Contractor's expense, that portion of the work and services for which such breach or otherwise has occurred and provided that any such claim or demand shall be made in writing to Contractor prior to the terminating of this Contract.
                    </Text>
                </View>

                <View style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 10, fontWeight: 600, color: '#334155', marginBottom: 4 }}>GENERAL TERMS</Text>
                    <Text style={{ fontSize: 9, color: '#475569', lineHeight: 1.4 }}>
                        This Construction Contract may not be assigned by either Party without written consent of the other Party. If any provision of this Construction Contract is found to be invalid, illegal, or unenforceable, the remaining portions shall remain in full force and effect. This Construction Contract is governed by the laws of the State of Tennessee and any legal proceedings will be maintained only in Williamson County, Tennessee.
                    </Text>
                </View>

                <View style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 10, fontWeight: 600, color: '#334155', marginBottom: 4 }}>2. OWNER'S RESPONSIBILITY</Text>
                    <Text style={{ fontSize: 9, color: '#475569', lineHeight: 1.4 }}>
                        The Owner will be responsible for properly marking the location of any privatized utilities, site lighting, low voltage lighting, invisible pet fences, underground drainage pipes, sprinkler and irrigation systems, and property lines that may be impacted by the Work if Leiper's Creek Timber Frames is the contracted installer. Contractor shall not be responsible for any damage occurring by reason of owner's failure to locate and mark all underground obstructions.
                    </Text>
                </View>

                <View style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 10, fontWeight: 600, color: '#334155', marginBottom: 4 }}>3. CONTRACTOR'S RESPONSIBILITY</Text>
                    <Text style={{ fontSize: 9, color: '#475569', lineHeight: 1.4 }}>
                        Contractor will perform the Work in a reasonably workmanlike manner and will undertake to complete the same within a reasonable period of time. Contractor will maintain, throughout the duration of this Construction Contract, all legally required licensure and permissions. The Contractor will purchase and maintain general liability insurance during the course of Work on the Property with duly licensed insurance companies in the amount of no less than $1 million dollars.
                    </Text>
                </View>

                <View style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 10, fontWeight: 600, color: '#334155', marginBottom: 4 }}>4. SCOPE OF WORK & CHANGE ORDERS</Text>
                    <Text style={{ fontSize: 9, color: '#475569', lineHeight: 1.4 }}>
                        The work that Contractor is obligated to perform for Owner is as set forth in "Your Investment" above. Any proposed changes during performance will be submitted in writing containing: proposal date, the change requested, explanation of how that will affect the cost or time of completion, and signed by the proposing party. When both parties have signed, it will be designated as a "Change Order" and incorporated into this Contract.
                    </Text>
                </View>

                <View style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 10, fontWeight: 600, color: '#334155', marginBottom: 4 }}>5. PAYMENTS</Text>
                    <Text style={{ fontSize: 9, color: '#475569', lineHeight: 1.4 }}>
                        Owner shall make the payments to Contractor for the Work as specified in "Your Investment". One and one-half (1.5%) percent interest will be added to the balance due each month as a late charge on any past due amount. Upon default of payment due, all costs of collection, court costs and reasonable attorney fees will be added to the final invoice amount. Payments are due at Leiper's Creek Timber Frames, 3745 Robinson Rd., Thompsons Station, TN 37179.
                    </Text>
                    {bondingClause(proposal.globalCosts?.bonding) && (
                        <>
                            <Text style={{ fontSize: 9, fontWeight: 600, color: '#334155', marginTop: 6, marginBottom: 2 }}>BONDING</Text>
                            <Text style={{ fontSize: 9, color: '#475569', lineHeight: 1.4 }}>
                                {bondingClause(proposal.globalCosts?.bonding)}
                            </Text>
                        </>
                    )}
                </View>

                <View style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 10, fontWeight: 600, color: '#334155', marginBottom: 4 }}>6. DISCLAIMER</Text>
                    <Text style={{ fontSize: 9, color: '#475569', lineHeight: 1.4 }}>
                        OWNER ACKNOWLEDGES THAT, EXCEPT AS SET FORTH HEREIN, ALL SERVICES ARE PROVIDED WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, FITNESS FOR A PARTICULAR PURPOSE. CONTRACTOR DOES NOT AUTHORIZE ANYONE TO MAKE A WARRANTY OF ANY KIND ON CONTRACTOR'S BEHALF.
                    </Text>
                </View>

                <View style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 10, fontWeight: 600, color: '#334155', marginBottom: 4 }}>7. ADDITIONAL PROVISIONS</Text>
                    <Text style={{ fontSize: 9, color: '#475569', lineHeight: 1.4 }}>
                        This section is reserved for any additional provisions agreed upon between the parties.
                    </Text>
                </View>

                {/* Footer */}
                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>{companyName} • {companyWebsite} • {proposalFooterLabel}</Text>
                    <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} style={styles.pageNumber} />
                </View>
            </Page>

            {/* Signature Page - Only included for signed PDFs */}
            {signature && (
                <Page size="LETTER" style={styles.page}>
                    {companyLogo && <Image src={companyLogo} style={{ width: 140, marginBottom: 30 }} />}

                    <View style={{
                        backgroundColor: '#2C3E2F',
                        padding: 20,
                        borderRadius: 8,
                        marginBottom: 30,
                        alignItems: 'center'
                    }}>
                        <Text style={{ color: '#ffffff', fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
                            Proposal Accepted
                        </Text>
                        <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>
                            This proposal has been reviewed and accepted by the client.
                        </Text>
                    </View>

                    <View style={{ marginBottom: 25 }}>
                        <Text style={{ fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 10 }}>
                            Client Signature
                        </Text>
                        <View style={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: 4,
                            padding: 15,
                            alignItems: 'center'
                        }}>
                            {signature.dataUrl && (
                                <Image
                                    src={signature.dataUrl}
                                    style={{ width: 200, height: 80, objectFit: 'contain', marginBottom: 10 }}
                                />
                            )}
                            <View style={{
                                width: '100%',
                                borderTop: '1px solid #334155',
                                paddingTop: 8,
                                marginTop: 5
                            }}>
                                <Text style={{ fontSize: 11, color: '#334155', textAlign: 'center' }}>
                                    {signature.signedBy}
                                </Text>
                            </View>
                        </View>
                    </View>

                    <View style={{
                        backgroundColor: '#f8fafc',
                        padding: 15,
                        borderRadius: 4,
                        marginBottom: 25
                    }}>
                        <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                            <Text style={{ fontSize: 10, color: '#64748b', width: 100 }}>Signed By:</Text>
                            <Text style={{ fontSize: 10, color: '#0f172a', fontWeight: 600 }}>{signature.signedBy}</Text>
                        </View>
                        <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                            <Text style={{ fontSize: 10, color: '#64748b', width: 100 }}>Date Signed:</Text>
                            <Text style={{ fontSize: 10, color: '#0f172a', fontWeight: 600 }}>
                                {(() => {
                                    const signedAt = signature.signedAt;
                                    const date = signedAt instanceof Date
                                        ? signedAt
                                        : (signedAt as any).toDate?.()
                                            ? (signedAt as any).toDate()
                                            : new Date();
                                    return date.toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    });
                                })()}
                            </Text>
                        </View>
                        <View style={{ flexDirection: 'row' }}>
                            <Text style={{ fontSize: 10, color: '#64748b', width: 100 }}>Proposal Total:</Text>
                            <Text style={{ fontSize: 10, color: '#0f172a', fontWeight: 600 }}>{formatCurrency(grandTotals.total)}</Text>
                        </View>
                    </View>

                    <View style={{
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        padding: 12,
                        borderRadius: 4,
                        marginBottom: 25
                    }}>
                        <Text style={{ fontSize: 9, color: '#166534', lineHeight: 1.4 }}>
                            By signing this proposal, the client acknowledges acceptance of all terms and conditions outlined in this document.
                            This signed copy serves as the official record of agreement between the parties.
                        </Text>
                    </View>

                    {/* Footer */}
                    <View style={styles.footer} fixed>
                        <Text style={styles.footerText}>{companyName} • {companyWebsite} • {signedFooterLabel}</Text>
                        <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} style={styles.pageNumber} />
                    </View>
                </Page>
            )}
        </Document >
    );
};

export default ProposalPdfDocument;
